/**
 * 1.62 — a number typed with its thousands grouped: "12,500", "1,250.5".
 *
 * Three jobs, none of them React: what a field shows for what was typed, where
 * the caret goes afterwards, and which plain number the text stands for. The
 * quote editor's fields (`GroupedNumberInput`) carry it to the screen.
 *
 * The comma and the point are the same in `he-IL` and `en-GB`, so they are
 * fixed here rather than asked of `Intl` on every key. Only digits and the
 * first point survive an edit; everything else is dropped. That is what makes a
 * paste work ("12,500", "₪ 12,500", "12 500") and a comma typed by hand a
 * no-op: the field puts the commas in, the typist does not. No field that uses
 * this takes a negative number, so a minus is one of the dropped characters.
 */

export interface GroupedNumberOptions {
  /** Digits kept before the point; the ones typed past it are dropped. */
  maxIntegerDigits?: number;
  /** Digits kept after the point — the 0.01 step these fields always declared. */
  maxDecimals?: number;
  /**
   * The edit was a forward delete. A comma it removes is put straight back, so
   * the caret has to step over it, or Delete could never get past one.
   */
  forward?: boolean;
}

export interface GroupedNumberEdit {
  text: string;
  /** Where the caret goes, as an offset into `text`. */
  caret: number;
}

interface Kept {
  char: string;
  /** Sat left of the caret, so the caret belongs after it once the text is regrouped. */
  before: boolean;
}

/** The digits and the first point of `raw`. */
function keep(raw: string, caret: number): Kept[] {
  const kept: Kept[] = [];
  let point = false;
  for (let index = 0; index < raw.length; index++) {
    const char = raw.charAt(index);
    if (char === '.' && !point) {
      point = true;
      kept.push({ char, before: index < caret });
    } else if (char >= '0' && char <= '9') {
      kept.push({ char, before: index < caret });
    }
  }
  return kept;
}

/** The offset just after the `count`-th digit or point of `text`; the commas are not counted. */
function caretAfter(text: string, count: number, forward: boolean): number {
  let caret = 0;
  let seen = 0;
  while (seen < count && caret < text.length) {
    if (text.charAt(caret) !== ',') seen++;
    caret++;
  }
  if (forward) while (text.charAt(caret) === ',') caret++;
  return caret;
}

/**
 * What the field shows once `raw` has been typed into it with the caret at
 * `caret`, and where the caret goes. `raw` is the field as the browser has just
 * changed it: commas and all, often with one character more or fewer.
 */
export function editGroupedNumber(raw: string, caret: number, options: GroupedNumberOptions = {}): GroupedNumberEdit {
  const { maxIntegerDigits = 9, maxDecimals = 2, forward = false } = options;
  const kept = keep(raw, caret);
  const point = kept.findIndex((one) => one.char === '.');

  const head = point < 0 ? kept : kept.slice(0, point);
  // "05" is 5, and the 0 of "0.5" stays.
  let zeros = 0;
  while (zeros < head.length - 1 && head[zeros]!.char === '0') zeros++;
  const whole = head.slice(zeros, zeros + maxIntegerDigits);
  // The point with the decimals that fit after it — a lone point is kept, it is the next key's.
  const tail = point < 0 || maxDecimals < 1 ? [] : kept.slice(point, point + 1 + maxDecimals);

  const grouped = whole
    .map((one) => one.char)
    .join('')
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const text = grouped + tail.map((one) => one.char).join('');
  const left = [...whole, ...tail].filter((one) => one.before).length;
  return { text, caret: caretAfter(text, left, forward) };
}

/** The plain number a grouped text stands for; nothing, or a lone point, is 0. */
export function parseGroupedNumber(text: string): number {
  return Number(keep(text, 0).map((one) => one.char).join('')) || 0;
}

/**
 * A number that arrives from outside — a saved quote, a reset — as the text the
 * field shows. Whole: the typing limits are for the keyboard, and a quote saved
 * before them keeps whatever it was written with.
 */
export function formatGroupedNumber(value: number): string {
  return Number.isFinite(value) ? value.toLocaleString('en-US', { maximumFractionDigits: 20 }) : '';
}
